/**
 * PHARMAKON — Trésorerie · the movement form, its confirmation, the operation drawer and the
 * « Mouvements » leaf: the copy of M1-M3, E-2 / E-3 / E-4 / E-6 / E-12 and L1 (TWP-20).
 *
 * Verbatim French UI copy of design/ux/02_comptes_caisses_mouvements.md §1.3, §10-§12 and
 * design/ux/03_reglements_journaux_tiers_dettes.md §11-§12 (the contract for E2E locators). Sentence
 * case; « DA » comes from the money formatter, never typed here; no amount is computed here
 * (03_UX_DESIGN P-5) — a sentence that names an amount receives it already formatted. Every
 * business word the server owns (types, modes, motifs, statuts, natures, origines, the preview's
 * refusals and résumé) is served by the API and shown verbatim; only the screen's own words live here.
 */

/** E-3 — the « Flux » band (UX 02 §1.3, §10.4). */
export const FLUX = Object.freeze({
  titre: "Flux",
  depuis: "Depuis",
  vers: "Vers",
  horsTresorerie: "Hors trésorerie",
  horsTresorerieAide: "client, fournisseur, CNAS, apport…",
  preciserCompte: "Préciser un compte (facultatif)",
  retirerCompte: "Retirer ce compte",
  montantASaisir: "montant à saisir",
  inverser: "Inverser",
  confirme: "Confirmé",
  previsionnel: "Prévisionnel",
  disponible: "Disponible",
  apres: "Après",
  calcul: "calcul…",
  sort: "sort",
  entre: "entre",
  insuffisant: "insuffisant",
  masque: "Masqué pour votre rôle",
  interne: "Mouvement interne",
  interneAide: "Ni une recette ni une dépense : l'argent change seulement de place.",
  totalPhysique: "Total physique",
  nonAffecte: "Non affecté",
  inchange: "inchangé",
  verrouille: (par) => `Défini par ${par}`,
});

/** E-2 — the account picker (UX 02 §10.5). */
export const PICKER = Object.freeze({
  placeholder: "Choisir un compte…",
  recherche: "Recherche…",
  aucun: "Aucun compte ne peut recevoir ce type de mouvement.",
  aucunPourRecherche: (q) => `Aucun compte ne correspond à « ${q} »`,
  aide: "La recherche ignore les accents ; Alt+↓ ouvre la liste.",
  creerCompte: "Créer un compte",
  virtuellesFiltrees: "Les caisses virtuelles d'une autre caisse ne sont proposées que via leur caisse parente.",
  enfantsSeulement: (parent) => `Seules les caisses virtuelles de « ${parent} » sont proposées.`,
  ligne2: (confirme, previsionnel) => `Confirmé ${confirme} · Prévisionnel ${previsionnel}`,
  session: (caissier) => `Session ouverte · ${caissier}`,
  sansSession: "Aucune session ouverte",
  masque: "Solde masqué pour votre rôle",
});

/** M1 — the one movement form (UX 02 §10). */
export const FORMULAIRE = Object.freeze({
  titreCreation: "Nouveau mouvement",
  titres: Object.freeze({
    ENCAISSEMENT: "Nouvel encaissement",
    DECAISSEMENT: "Nouveau décaissement",
    TRANSFERT: "Nouveau transfert",
  }),
  titreAffectation: "Affecter à une caisse virtuelle",
  titreModification: (numero) => (numero ? `Modifier l'opération ${numero}` : "Modifier le brouillon"),
  statut: "Statut",
  champs: Object.freeze({
    type: "Type",
    source: "Depuis",
    destination: "Vers",
    montant: "Montant",
    mode: "Mode de règlement",
    motif: "Motif",
    tiers: "Tiers",
    reference: "Référence",
    dateOperation: "Date de pièce",
    dateEcheance: "Date d'échéance",
    dateConfirmation: "Date de confirmation",
    confirmerMaintenant: "Confirmer maintenant",
    note: "Note",
    documentSource: "Document source",
    nature: "Nature",
    motifModification: "Motif de modification",
  }),
  aides: Object.freeze({
    referenceRequise: (mode) => `obligatoire pour un ${mode.toLowerCase()}`,
    dateConfirmation: "La date à laquelle l'argent a bougé : les soldes sont mis à jour à cette date.",
    confirmerDesactive: "Sera validé par le propriétaire",
    tiersRequis: "Ce motif demande un tiers.",
    motifAucun: "Aucun motif actif ne s'applique à ce type de mouvement — le propriétaire les règle dans Paramètres de trésorerie.",
    motifRecherche: "Tapez pour filtrer ; les motifs sont rangés par groupe.",
    tiersRecherche: "Tapez au moins 2 lettres du nom ou du code (F4).",
    tiersAucun: (q) => `Aucun tiers actif ne correspond à « ${q} »`,
    natureProprietaire: "La nature se reclasse par le propriétaire seulement.",
    plusOptions: "Plus d'options",
    moinsOptions: "Moins d'options",
    natureDefaut: (libelle) => `Par défaut : ${libelle}`,
    aucunDocument: "— (aucun)",
    typeFige: "Pour changer le type, annulez l'opération puis créez-en une nouvelle.",
    motifModification: (n) => `Obligatoire sur une opération confirmée (au moins ${n} caractères).`,
    motifModificationBrouillon: "Facultatif sur un brouillon.",
    avant: (valeur) => `Avant : ${valeur}`,
    retablir: "Ctrl+Retour arrière rétablit la valeur d'avant.",
    gereParDocument: (doc) => `Géré par ${doc} — modifier depuis la pièce d'origine.`,
    sansBrouillon: "Ce paiement se confirme en une fois : il n'existe pas en brouillon.",
  }),
  /** A-UX3-10 — the document line under the Flux band (a payment owned by one document: a charge…). */
  ligneDocument: Object.freeze({
    resteAvant: "reste avant",
    paye: "payé",
    resteApres: "reste après",
    masque: "montants masqués pour votre rôle",
  }),
  annonces: Object.freeze({
    sourceRetiree: "La source a été retirée : un encaissement n'a pas de source obligatoire.",
    destinationRetiree: "La destination a été retirée : un décaissement n'a pas de destination obligatoire.",
    compteDeplace: (libelle, cote) => `« ${libelle} » passe en « ${cote} » : c'est le compte de ce mouvement.`,
    motifRetire: (libelle) => `Le motif « ${libelle} » a été retiré : il ne s'applique pas à ce type de mouvement.`,
    modeChange: (avant, apres) => `Le mode « ${avant} » a été remplacé par « ${apres} » : il ne correspond pas aux comptes choisis.`,
    modeSuit: (apres) => `Mode de règlement : « ${apres} », d'après les comptes choisis.`,
    inverse: "La source et la destination ont été inversées.",
    preset: (libelle) => `Prérempli depuis ${libelle}.`,
    affectation: (parent) => `Affectation depuis « ${parent} » : seules ses caisses virtuelles sont proposées.`,
    brouillonForce: "Votre rôle ne permet pas de confirmer ce mouvement : il sera enregistré en brouillon pour le propriétaire.",
  }),
  resume: "Résumé",
  resumeVide: "Choisissez le type, les comptes et le montant : le résumé du mouvement s'affiche ici.",
  apercuIndisponible: "L'aperçu du serveur est indisponible : le mouvement sera vérifié à l'enregistrement.",
  erreursResume: (n) => (n === 1 ? "1 point à corriger avant d'enregistrer :" : `${n} points à corriger avant d'enregistrer :`),
  horsLigne: "Hors ligne — le mouvement n'a pas été enregistré.",
  reessayer: "Réessayer",
  attente: "Calcul de l'aperçu en cours…",
  boutons: Object.freeze({
    annuler: "Annuler",
    brouillon: "Enregistrer le brouillon",
    confirmer: "Confirmer",
    enregistrerModification: "Enregistrer la modification",
    enregistrerEtConfirmer: "Enregistrer et confirmer",
  }),
  classes: Object.freeze({
    DESCRIPTIF: "Modification descriptive : aucun solde ne change.",
    ANALYTIQUE: "Aucun solde ne change ; les analyses utiliseront les nouvelles valeurs.",
    FINANCIER: (date) =>
      `Les soldes sont corrigés à la date d'origine (${date}). Des écritures de correction et de nouvelles écritures seront ajoutées ; les écritures d'origine restent visibles.`,
    aucune: "Aucune modification pour l'instant.",
  }),
  bandeauConfirmee: (date) =>
    `Opération confirmée le ${date}. Vous pouvez la modifier directement ; chaque changement est conservé dans l'historique (qui, quand, avant, après).`,
  bandeauBrouillon: "Brouillon : aucun solde ne bouge avant sa confirmation. Chaque changement est conservé dans l'historique.",
  conflit: Object.freeze({
    titre: (qui, heure) =>
      qui ? `Cette opération a été modifiée par ${qui} à ${heure} pendant votre saisie.` : `Cette opération a été modifiée à ${heure} pendant votre saisie.`,
    voir: "Voir ses changements",
    masquer: "Masquer ses changements",
    reprendre: "Reprendre mes valeurs sur la nouvelle version",
    aucunChangement: "Aucun champ visible n'a changé (un geste comme une confirmation a pu passer).",
  }),
  confirmation: Object.freeze({
    titre: Object.freeze({
      ENCAISSEMENT: (m) => `Confirmer cet encaissement de ${m} ?`,
      DECAISSEMENT: (m) => `Confirmer ce décaissement de ${m} ?`,
      TRANSFERT: (m) => `Confirmer ce transfert de ${m} ?`,
    }),
    titreModification: (numero) => `Enregistrer la modification de ${numero ?? "l'opération"} ?`,
    flux: "Flux",
    motif: "Motif",
    date: "Date de confirmation",
    mode: "Mode",
    tiers: "Tiers",
    champ: "Champ",
    avantApres: (avant, apres) => `${avant} → ${apres}`,
  }),
  succes: Object.freeze({
    brouillon: (montant, flux) => `Brouillon enregistré — ${montant} ${flux}`,
    confirme: (montant, flux) => `Mouvement confirmé — ${montant} ${flux}`,
    rejoue: "Ce mouvement était déjà enregistré : rien n'a été saisi deux fois.",
    modifie: (numero) => `Opération ${numero ?? ""} modifiée — l'historique garde l'avant et l'après.`.replace("  ", " "),
    inchange: "Aucun changement à enregistrer.",
    ouvrir: "Ouvrir",
    affecter: "Affecter maintenant",
  }),
});

/** M2 — statuses, E-4 ConfirmPopover, bulk confirmation, cancellation (UX 02 §11). */
export const CONFIRMATION = Object.freeze({
  popoverTitre: "Confirmer le brouillon",
  echeancePrevue: (date) => `Échéance prévue : ${date}`,
  date: "Date de confirmation",
  confirmer: "Confirmer",
  annuler: "Annuler",
  dejaConfirmee: "Une opération confirmée ne se « déconfirme » pas : annulez-la (« Annuler l'opération… »).",
  cocher: (numero) => `Confirmer ${numero}`,
  succes: (numero) => `Opération ${numero} confirmée`,
  groupe: Object.freeze({
    titre: (n) => (n === 1 ? "Confirmer 1 opération ?" : `Confirmer ${n} opérations ?`),
    message: "Chaque opération est confirmée séparément : une opération refusée n'empêche pas les autres.",
    date: "Date de confirmation (toutes les opérations sélectionnées)",
    colonnes: Object.freeze({ operation: "Opération", montant: "Montant (DA)", flux: "Depuis ⟶ Vers", echeance: "Échéance" }),
    confirmer: "Confirmer la sélection",
    resultat: (ok, ko) =>
      `${ok === 1 ? "1 confirmée" : `${ok} confirmées`}${ko > 0 ? ` · ${ko === 1 ? "1 refusée" : `${ko} refusées`}` : ""}`,
    refusee: (numero) => `${numero} — refusée`,
    ouvrir: "Ouvrir",
    fermer: "Fermer",
    aucune: "Sélectionnez au moins un brouillon (Espace) avant de confirmer.",
    trop: (max) => `Au plus ${max} opérations par confirmation groupée : réduisez la sélection.`,
    sansSomme: "Les soldes de chaque compte sont recalculés par le serveur, opération par opération.",
  }),
  annulation: Object.freeze({
    titre: (numero) => (numero ? `Annuler l'opération ${numero} ?` : "Annuler le brouillon ?"),
    message: (type, montant, source, destination, date) =>
      `${type} de ${montant}${source ? ` de ${source}` : ""}${destination ? ` vers ${destination}` : ""} sera annulé : les soldes reviennent à leur valeur du ${date}. L'opération reste visible, barrée, avec son historique.`,
    messageBrouillon: "Le brouillon sera annulé : il reste visible dans « Annulées », avec son historique. Aucun solde ne bouge.",
    motif: "Motif d'annulation",
    motifAide: (n) => `Au moins ${n} caractères : il est conservé dans l'historique.`,
    nePasAnnuler: "Ne pas annuler",
    confirmer: "Annuler l'opération",
    confirmerBrouillon: "Annuler le brouillon",
    succes: (numero) => `Opération ${numero ?? ""} annulée — elle reste visible, barrée.`.replace("  ", " "),
    gereePar: (libelle) => `Cette opération est gérée par ${libelle} : annulez-la depuis sa pièce d'origine.`,
    ouvrirPiece: "Ouvrir la pièce",
  }),
});

/** E-6 — DueChip (UX 02 §14.2). */
export const ECHEANCE = Object.freeze({
  aujourdhui: "aujourd'hui",
  dans: (n) => (n === 1 ? "dans 1 jour" : `dans ${n} jours`),
  retard: (n) => `en retard de ${n} j`,
});

/** M3 — the operation drawer (UX 02 §12, UX 03 §11.3). */
export const TIROIR_OPERATION = Object.freeze({
  titre: (numero) => (numero ? `Opération ${numero}` : "Brouillon d'opération"),
  chargement: "Chargement de l'opération…",
  erreur: "L'opération n'a pas pu être lue.",
  introuvable: "Élément introuvable dans cette officine.",
  confirmeLe: (date) => `Confirmé le ${date}`,
  annuleeLe: (date) => `Annulée le ${date}`,
  modifiee: "Modifiée",
  aValider: "À valider par le propriétaire",
  masque: "Masqué pour votre rôle",
  voirHistorique: "Voir l'historique (Alt+H)",
  // TWP-29 — a row gesture of the journal (Alt+O / Suppr) the server does not offer on this operation.
  gesteIndisponible: Object.freeze({
    modifier: "La modification n'est pas proposée pour cette opération.",
    annuler: "L'annulation n'est pas proposée pour cette opération.",
  }),
  systeme: (origine) => (origine ? `système · ${origine}` : "système"),
  saisiPar: (qui, quand) => `Saisi par ${qui} ${quand}`,
  confirmePar: (qui, quand) => `Confirmé par ${qui} ${quand}`,
  annulePar: (qui, quand, motif) => `Annulé par ${qui} ${quand}${motif ? ` — motif : « ${motif} »` : ""}`,
  ligne: Object.freeze({
    motif: (m) => `Motif : ${m}`,
    tiers: (t) => `Tiers : ${t}`,
    reference: (r) => `Réf. ${r}`,
    sansReference: "Réf. —",
    piece: (d) => `Pièce ${d}`,
    echeance: (d) => `Échéance ${d}`,
    sansEcheance: "Échéance —",
  }),
  gerePar: (libelle) => `Géré par ${libelle}`,
  actions: Object.freeze({
    modifier: "Modifier",
    confirmer: "Confirmer",
    annuler: "Annuler l'opération…",
    annulerBrouillon: "Annuler le brouillon…",
    imprimer: "Imprimer la pièce",
    precedent: "Opération précédente",
    suivant: "Opération suivante",
  }),
  ongletsLabel: "Détails de l'opération",
  onglets: Object.freeze({
    detail: "Détail",
    ventilation: "Ventilation",
    document: "Document source",
    historique: "Historique",
    ecritures: "Écritures",
  }),
  detail: Object.freeze({
    type: "Type",
    statut: "Statut",
    montant: "Montant",
    mode: "Mode de règlement",
    motif: "Motif",
    tiers: "Tiers",
    reference: "Référence",
    dateOperation: "Date de pièce",
    dateEcheance: "Date d'échéance",
    dateConfirmation: "Date de confirmation",
    nature: "Nature",
    origine: "Origine",
    note: "Note",
    numero: "N°",
    aucun: "—",
  }),
  ventilation: Object.freeze({
    grille: "Factures réglées par cette opération",
    colonnes: Object.freeze({ facture: "Facture", date: "Date", ttc: "Montant TTC (DA)", impute: "Imputé (DA)", statut: "État" }),
    vide: "Aucune facture n'est imputée sur ce règlement.",
    proposee: "Ventilation proposée : elle est recalculée à la confirmation.",
    gerer: "La ventilation se modifie depuis Règlements.",
  }),
  historique: Object.freeze({
    vide: "Aucune entrée d'historique.",
    creee: (statut) => `Statut à la création : ${statut.toLowerCase()}`,
    motif: (m) => `Motif : « ${m} »`,
    champ: (libelle, avant, apres) => `${libelle} : ${avant} → ${apres}`,
    corrigees: (liste) => `Écritures corrigées : ${liste}`,
    nouvelles: (liste) => `Nouvelles écritures : ${liste}`,
    vide_valeur: "—",
  }),
  ecritures: Object.freeze({
    grille: "Écritures de l'opération",
    colonnes: Object.freeze({ numero: "N°", compte: "Compte", sens: "Sens", montant: "Montant (DA)", date: "Date de valeur", statut: "État" }),
    entree: "Entrée",
    sortie: "Sortie",
    annulee: "Compensée",
    vivante: "Active",
    correction: (n) => (n === 1 ? "Écritures de correction (1)" : `Écritures de correction (${n})`),
    vide: "Aucune écriture : un brouillon n'en a pas avant sa confirmation.",
  }),
  document: Object.freeze({
    titre: "Document source",
    ouvrir: "Ouvrir",
    aucun: "Saisie manuelle — aucun document source.",
    accesReserve: "accès réservé",
    aVerifier: "À vérifier",
  }),
  fil: "Panneaux ouverts",
});

/** The A4 « pièce » of an operation (WP-19 print layer). */
export const PIECE = Object.freeze({
  titre: "Pièce de trésorerie",
  brouillon: "Brouillon — aucun solde n'a bougé",
  annulee: "Annulée",
  meta: Object.freeze({
    type: "Type",
    mode: "Mode",
    motif: "Motif",
    reference: "Référence",
    piece: "Date de pièce",
    echeance: "Échéance",
    confirmation: "Date de confirmation",
    saisi: "Saisi par",
    confirme: "Confirmé par",
  }),
  tiers: "Tiers",
  colonnes: Object.freeze({ cote: "Compte", libelle: "Libellé", montant: "Montant (DA)" }),
  sorties: "Sort de",
  entrees: "Entre dans",
  horsTresorerie: "Hors trésorerie",
  montant: "Montant",
  signatures: "Visa de l'émetteur : ______________________     Visa du responsable : ______________________",
});

/** J1 (minimal, until TWP-29) — the « Mouvements » leaf. */
export const MOUVEMENTS = Object.freeze({
  // TWP-23 — the journal filtered on one motif (« Utilisé » of Paramètres › Motifs).
  filtreMotif: (libelle) => `Motif : ${libelle}`,
  filtreMotifInconnu: "Motif choisi",
  retirerFiltre: "Retirer le filtre",
  videMotif: "Aucun mouvement avec ce motif dans cette vue.",
  titre: "Mouvements",
  sousTitre: "Tous les mouvements d'argent de l'officine, du plus récent au plus ancien.",
  nouveau: "Nouveau…",
  nouveauMenu: Object.freeze({
    ENCAISSEMENT: "Encaissement",
    DECAISSEMENT: "Décaissement",
    TRANSFERT: "Transfert",
  }),
  vuesLabel: "Vues des mouvements",
  vues: Object.freeze({
    tous: "Tous",
    encaissements: "Encaissements",
    decaissements: "Décaissements",
    transferts: "Transferts",
    caisse: "Mouvements de caisse",
    "a-confirmer": "À confirmer",
    annulees: "Annulées",
  }),
  grille: "Mouvements de l'officine",
  colonnes: Object.freeze({
    confirme: "Confirmé",
    confirmeCourt: "Conf.",
    date: "Date",
    numero: "N°",
    type: "Type",
    motif: "Motif",
    flux: "Depuis ⟶ Vers",
    tiers: "Tiers",
    montant: "Montant (DA)",
    mode: "Mode",
    statut: "Statut",
    auteur: "Saisi par",
  }),
  vide: Object.freeze({
    tous: "Aucun mouvement pour l'instant.",
    "a-confirmer": "Aucun brouillon à confirmer.",
    annulees: "Aucune opération annulée.",
    autre: "Aucun mouvement dans cette vue.",
    action: "Nouveau mouvement (F3)",
  }),
  plus: "Plus anciens",
  totaux: Object.freeze({
    label: "Total de la vue (toutes les pages)",
    nbLabel: "Confirmées",
    nb: (n) => (n === 1 ? "1 opération" : `${n} opérations`),
    nonComptes: "(non comptés)",
    encaissements: "Encaissements",
    decaissements: "Décaissements",
    net: "Flux net",
    internes: "Mouvements internes",
    brouillons: "Brouillons",
    annulees: "Annulées",
    masques: (n) => (n === 1 ? "1 montant masqué pour votre rôle" : `${n} montants masqués pour votre rôle`),
  }),
  selection: (n) => (n === 1 ? "1 brouillon sélectionné" : `${n} brouillons sélectionnés`),
  confirmerSelection: "Confirmer la sélection",
  aideSelection: "Espace coche un brouillon ; F9 confirme la sélection.",
  // UX 03 §9.10 (TWP-29) — the 403 of the journal.
  interdit: "Votre rôle ne donne pas accès au journal de la trésorerie.",
  prevu: "prévu",
  par: (qui) => `par ${qui}`,
  horsTresorerie: "Hors trésorerie",
  systeme: "Système",
  misAJour: (heure) => `Mis à jour ${heure}`,
});
